// components/ApproverCommentsSection.jsx
import React, { useRef, useState } from "react";
import ReactQuill from "react-quill";

const ApproverCommentsSection = ({
    selectedRatesTab,
    rfqData,
    userProfile,
    approverComment,
    setApproverComment,
}) => {
    const quillRef = useRef(null);
    const [showCommentInput, setShowCommentInput] = useState(false);

    if (!selectedRatesTab) return null;

    const allApproverIds =
        rfqData?.approvalDetails?.flatMap((level) =>
            level.approvers?.map((a) => (typeof a === "string" ? a : a.userId))
        ) || [];

    const userIndex = allApproverIds.indexOf(userProfile?.userId);

    const getAttachmentUrl = (attachment) => attachment?.Content || attachment?.url || attachment?.fileUrl || "";
    const getAttachmentExtension = (attachment) => {
        const fileName = attachment?.Name || "";
        const lastIndex = fileName.lastIndexOf(".");
        return lastIndex !== -1 ? fileName.substring(lastIndex + 1).toLowerCase() : "pdf";
    };

    const handleViewAttachment = (attachment) => {
        const url = getAttachmentUrl(attachment);
        if (!url) return;
        window.open(url, "_blank", "noopener,noreferrer");
    };

    const handleDownloadAttachment = (attachment) => {
        const url = getAttachmentUrl(attachment);
        if (!url) return;

        const link = document.createElement("a");
        link.href = url;
        link.download = attachment?.Name || "approval-attachment";
        link.target = "_blank";
        link.rel = "noopener noreferrer";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    return (<>
        {rfqData?.commentsToApprover?.length > 0 && 
        <div className="my-4 p-4 border border-gray-300 shadow-md bg-white">
            <h3 className="text-sm font-semibold mb-2">Comments for Approver:</h3>
            {/* Comment trail display */}
            {rfqData?.commentsToApprover.map((commentObj, index) => {
                const attachments = Array.isArray(commentObj?.attachments) ? commentObj.attachments : [];

                return (
                    <div key={index} className="mb-1">
                        <div className="text-sm leading-relaxed">
                            <span dangerouslySetInnerHTML={{ __html: commentObj.comment }} />
                        </div>
                        <p className="text-xs text-gray-600 mt-1">
                            Added by: <span className="font-medium">{commentObj.addedByName}</span> &nbsp;
                            {(() => {
                                const dateTime = new Date(`${commentObj.date}T${commentObj.time}`);
                                const timeStr = dateTime.toLocaleTimeString([], {
                                    hour: '2-digit',
                                    minute: '2-digit'
                                });
                                const dateStr = `${dateTime.getDate()}/${dateTime.getMonth() + 1}/${dateTime.getFullYear()}`;
                                return `At: ${timeStr} ${dateStr}`;
                            })()}
                        </p>

                        {attachments.length > 0 && (
                            <div className="mt-2 flex flex-wrap gap-2">
                                {attachments.map((attachment, attachmentIndex) => {
                                    const extension = getAttachmentExtension(attachment);

                                    return (
                                        <div
                                            key={`${attachment?.Name || "attachment"}-${attachmentIndex}`}
                                            className="flex items-center gap-2 w-fit max-w-full rounded-full border border-[#D9D9D9] bg-white px-2 py-1 text-xs"
                                        >
                                            <img
                                                src={`/assets/icons/${extension}.png`}
                                                alt={extension}
                                                className="h-5 w-5 flex-shrink-0"
                                                onError={(event) => {
                                                    event.currentTarget.src = "/assets/icons/pdf.png";
                                                }}
                                            />
                                            <span
                                                className="max-w-[220px] truncate font-medium text-[#434343]"
                                                title={attachment?.Name || `Attachment ${attachmentIndex + 1}`}
                                            >
                                                {attachment?.Name || `Attachment ${attachmentIndex + 1}`}
                                            </span>
                                            <button
                                                type="button"
                                                className="ml-1 inline-flex items-center gap-1 text-blue-700 hover:text-blue-900"
                                                onClick={() => handleViewAttachment(attachment)}
                                            >
                                                <svg className="h-3.5 w-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                    <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7Z" />
                                                    <circle cx="12" cy="12" r="3" />
                                                </svg>
                                                <span>View</span>
                                            </button>
                                            <button
                                                type="button"
                                                className="inline-flex items-center gap-1 text-green-700 hover:text-green-900"
                                                onClick={() => handleDownloadAttachment(attachment)}
                                            >
                                                <svg className="h-3.5 w-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                    <path d="M12 3v12" />
                                                    <path d="m7 10 5 5 5-5" />
                                                    <path d="M5 21h14" />
                                                </svg>
                                                <span>Download</span>
                                            </button>
                                        </div>
                                    );
                                })}
                            </div>
                        )}

                        {index !== rfqData?.commentsToApprover.length - 1 && (
                            <hr className="my-2 border-gray-300" />
                        )}
                    </div>

                );
            })}

            {/* Comment input field */}
            {userIndex !== -1 && rfqData?.status !== "CLOSED" && (
                <div className="mt-2">
                    {!showCommentInput ? (
                        <button
                            className="text-blue-700 text-xs py-1 underline italic"
                            onClick={() => setShowCommentInput(true)}
                        >
                            Add Additional Notes
                        </button>
                    ) : (
                        <div className="mt-2">
                            <p className="text-sm mb-1">Additional Notes:</p>
                            <style>
                                {`
                                .no-padding .ql-container {
                                    height: 64px !important;
                                    padding: 0 !important;
                                    border: none !important;
                                    overflow: hidden !important;
                                }
                                .no-padding .ql-editor {
                                    height: 64px !important;
                                    max-height: 64px !important;
                                    overflow: hidden !important;
                                    padding: 4px 6px !important;
                                    font-size: 0.875rem !important;
                                    line-height: 1.25rem !important;
                                    white-space: normal !important;
                                }
                                `}
                            </style>
                            <ReactQuill
                                ref={quillRef}
                                value={approverComment}
                                onChange={(value) => {
                                    const editor = quillRef.current?.getEditor();
                                    const plainText = editor.getText();
                                    const textLength = plainText.trim().length;

                                    if (textLength <= 500) {
                                        setApproverComment(value);
                                    } else {
                                        const truncated = plainText.trim().substring(0, 500);
                                        editor.setText(truncated);
                                        setApproverComment(editor.root.innerHTML);
                                    }
                                }}
                                theme="snow"
                                className="min-h-[65px] h-[65px] border border-gray-300 overflow-hidden mt-1 p-0 font-normal word-break whitespace-normal focus:outline-none focus:ring-0 text-sm w-full no-padding"
                                modules={{ toolbar: false }}
                            />
                            <p className="italic text-xs font-normal pt-1 text-gray-500">
                                Note: Max 500 characters allowed (
                                {quillRef.current?.getEditor().getText().trim().length || 0}/500)
                            </p>

                            {/* Cancel Button */}
                            <div className="mt-1">
                                <button
                                    className="text-xs text-red-600 underline"
                                    onClick={() => {
                                        setShowCommentInput(false);
                                        setApproverComment(""); // optionally clear comment
                                    }}
                                >
                                    Cancel
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            )}
         </div>}
         </>
    );
};

export default ApproverCommentsSection;
